import {flow, IDisposer, Instance} from 'mobx-state-tree';
import {when} from 'mobx';
import {TransientEventTypeEnum} from '@joyrideautos/auction-core/src/types/events/transient';
import {now} from 'mobx-utils';
import BaseViewModel from '../../BaseViewModel';
import {reduceString} from '@joyrideautos/auction-utils/src/stringUtils';
import {filterVehiclesOnListingAuction} from '../../types/auction/Auction';
import {AuctionReference} from '../../types/auction/Refs';
import {AnalyticsFavoritesEventEnum} from '@joyrideautos/ui-services/src/services/AnalyticsService';

const AuctionStatus = {
    LIVE_AUCTION: 'live-auction',
    POST_AUCTION: 'post-auction',
    PRE_AUCTION: 'pre-auction',
};

const MAX_AUCTION_CARD_TITLE_LENGTH = 20;

export const AuctionCardModel = BaseViewModel.named('AuctionCardModel')
    .props({
        auction: AuctionReference,
    })
    .volatile(() => ({
        disposers: [] as IDisposer[],
    }))
    .views((self) => {
        const getItemsStore = () => {
            return self.itemsStoreFactory.getItemsStoreForAuction({
                regionId: self.auction.regionId,
                auctionId: self.auction.auctionId,
            });
        };

        return {
            get title() {
                if (self.auction.isSequenceAuctionType) {
                    return this.formattedStartDate;
                }
                return self.auction.name
                    ? reduceString(self.auction.name, MAX_AUCTION_CARD_TITLE_LENGTH)
                    : `Ends ${self.auction.settings.formattedEndDateListing}`;
            },
            get formattedStartDate() {
                return self.auction.settings.formattedStartByAuctionType;
            },
            get isFavorite() {
                return self.favoritesStore.isFavoriteAuction(self.auction.auctionId);
            },
            get isRestricted() {
                return self.auction.isRestricted;
            },
            get isSellerOrManager() {
                return self.sellerRoleStore.hasSellerRoleForAnyCompany(self.authUser?.uid, this.sellers);
            },
            get shouldShowRequestAccessButton() {
                return (
                    !self.authUserStore.isLoggedInUserAnonymous &&
                    !self.auction.ended &&
                    this.isRestricted &&
                    !this.accessGranted &&
                    !this.isSellerOrManager
                );
            },
            get isEnded() {
                return self.auction.ended;
            },
            get sellers(): string[] {
                return self.auction.sellers ? [...self.auction.sellers.keys()] : [];
            },
            get accessGranted() {
                const series = self.auctionSeriesStore.findSeriesByKey(self.auction.auctionSeries);
                if (!series) {
                    return true;
                }
                return self.biddersStatuses.hasAccessForSeries(series.key);
            },
            get itemsCount() {
                const items = getItemsStore().getAllItems().filter(filterVehiclesOnListingAuction(self.auction));
                return items.length;
            },
            get isLive() {
                if (!this.itemsCount) {
                    return false;
                }
                if (self.auction?.ended) {
                    return false;
                }
                if (!self.auction.settings.startByAuctionTypeAsDate) {
                    return false;
                }
                return now() >= self.auction.settings.startByAuctionTypeAsDate?.getTime();
            },
            get liveAuctionTagText() {
                return self.auction?.liveAuctionTagText;
            },
            get daysBeforeStart() {
                return self.auction.settings.daysBeforeStart;
            },
            get daysToScheduleVehicles() {
                return self.auction.settings.daysToScheduleVehicles;
            },
            get auctionSeries() {
                const auctionSeries = self.auctionSeriesStore.findSeriesByKey(self.auction.auctionSeries);
                return auctionSeries;
            },
            get location() {
                const region = self.regionsStore.findRegion(self.auction.regionId);
                return `${region?.renderedDisplayName}, ${region?.stateId}`;
            },
            get auctionStatus() {
                if (this.isEnded) {
                    return AuctionStatus.POST_AUCTION;
                } else if (this.isLive) {
                    return AuctionStatus.LIVE_AUCTION;
                } else {
                    return AuctionStatus.PRE_AUCTION;
                }
            },
        };
    })
    .actions((self) => {
        return {
            toggleFavorite: flow(function* () {
                if (self.auction) {
                    const result = yield self.favoritesStore.toggleFavoriteAuction(self.auction);
                    const eventType = result
                        ? TransientEventTypeEnum.FAVORITE_AUCTION_ADDED
                        : TransientEventTypeEnum.FAVORITE_AUCTION_REMOVED;
                    self.eventsStore.addTransientEvents({type: eventType, params: {itemTitle: self.auction.title}});
                    // Firebase Analytics
                    const eventName = result
                        ? AnalyticsFavoritesEventEnum.ADD_TO_WISHLIST
                        : AnalyticsFavoritesEventEnum.REMOVE_FROM_WISHLIST;
                    yield self.rootStore.analyticsService.logFavoritesEvent(
                        eventName,
                        'auction',
                        self.auction.auctionId,
                        self.auction.regionId,
                        self.auction.auctionId
                    );
                }
            }),
            afterCreate() {
                self.disposers.push(
                    when(
                        () => Boolean(self.auctionSeriesStore.subscribeStatus.isReady && self.auctionSeries),
                        () => {
                            self.disposers.push(self.biddersStatuses.subscribe(self.auctionSeries!.key, self.sellers));
                        }
                    )
                );
            },
            beforeDestroy() {
                self.disposers.forEach((d) => d());
                if (self.auction) {
                    self.itemsStoreFactory.destroyItemsStoreForAuction(self.auction.auctionPath);
                }
            },
        };
    });

export interface AuctionCardModelType extends Instance<typeof AuctionCardModel> {}
